import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetIdentityHeaderQuery, useGetSingleIdentityQuery } from 'store/features/process/processApi';

const useGetIdentityPermissionHook = ({ processCode, id }) => {
	const navigate = useNavigate();
	const companyCode = useSelector((state) => state.profile.profileData?.creatorOrganizationId);
	const userId = useSelector((state) => state.profile.profileData?.userId);

	const {
		data: headerData,
		isUninitialized: isHeaderUninitialized,
		isLoading: isHeaderLoading,
		isSuccess: isHeaderSuccess,
		isError: isHeaderError,
	} = useGetIdentityHeaderQuery({ companyCode, suffixCode: '', operation: 'get-single', processCode }, { skip: !companyCode || !processCode });

	const {
		data: singleData,
		isUninitialized: isSingleUninitialized,
		isLoading: isSingleLoading,
		isSuccess: isSingleSuccess,
		isError: isSingleError,
	} = useGetSingleIdentityQuery(
		{
			suffixCode: '',
			_id: id,
			operation: 'get-single',
			processCode,
		},
		{ skip: !id || !processCode, refetchOnMountOrArgChange: true, keepUnusedDataFor: 0 }
	);

	// Early exit if missing args
	useEffect(() => {
		if (!processCode || !id) navigate(-1);
	}, [processCode, id]);

	// Permission computation (pure logic)
	const approverList = singleData?.approverUserId || [];
	const hasApprovalPermission = approverList.includes(userId);

	const permissions = useMemo(
		() => ({
			reject: hasApprovalPermission,
			approve: hasApprovalPermission,
			read: headerData?.permissionRead === 'allow',
			write: headerData?.permissionWrite === 'allow',
			edit: headerData?.permissionUpdate === 'allow',
			delete: headerData?.permissionDelete === 'allow',
		}),
		[headerData, hasApprovalPermission]
	);

	return {
		singleIdentityData: singleData,
		isSingleUninitialized: isHeaderUninitialized || isSingleUninitialized,
		isSingleIdentityLoading: isSingleLoading || isHeaderLoading,
		isSingleIdentitySuccess: isHeaderSuccess && isSingleSuccess,
		isSingleIdentityError: isHeaderError || isSingleError,
		permissions,
		companyCode,
		userId,
	};
};

export default useGetIdentityPermissionHook;
